import React from 'react';
import styled from 'styled-components';
import { DrillDownBreadcrumb } from '../../types/drilldown';
import { Colors, Spacing, FontSizes, BorderRadius } from '../../constants/theme';

interface DrillDownBreadcrumbsProps {
  breadcrumbs: DrillDownBreadcrumb[];
  onNavigateToLevel: (breadcrumbIndex: number) => void;
}

const BreadcrumbsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${Spacing.xs}px;
  margin-bottom: ${Spacing.lg}px;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: ${Colors.backgroundLight};
  border-radius: ${BorderRadius.sm}px;
  border: 1px solid ${Colors.borderLight};
`;

const BreadcrumbItem = styled.button<{ isLast: boolean }>`
  display: flex;
  align-items: center;
  background: ${props => props.isLast ? Colors.primary : 'transparent'};
  color: ${props => props.isLast ? Colors.textOnPrimary : Colors.textSecondary};
  border: none;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.xs}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${props => props.isLast ? 600 : 400};
  cursor: ${props => props.isLast ? 'default' : 'pointer'};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.isLast ? Colors.primary : Colors.surfaceHover};
    color: ${props => props.isLast ? Colors.textOnPrimary : Colors.textPrimary};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const Separator = styled.span`
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.sm}px;
  margin: 0 ${Spacing.xs}px;
`;

export const DrillDownBreadcrumbs: React.FC<DrillDownBreadcrumbsProps> = ({
  breadcrumbs,
  onNavigateToLevel,
}) => {
  if (breadcrumbs.length === 0) return null;

  return (
    <BreadcrumbsContainer>
      {breadcrumbs.map((breadcrumb, index) => (
        <React.Fragment key={`${breadcrumb.level}-${index}`}>
          <BreadcrumbItem
            isLast={index === breadcrumbs.length - 1}
            onClick={() => onNavigateToLevel(index)}
            disabled={index === breadcrumbs.length - 1}
            aria-current={index === breadcrumbs.length - 1 ? 'page' : undefined}
          >
            {breadcrumb.label}
            {breadcrumb.value && ` (${breadcrumb.value})`}
          </BreadcrumbItem>
          {index < breadcrumbs.length - 1 && (
            <Separator aria-hidden="true">→</Separator>
          )}
        </React.Fragment>
      ))}
    </BreadcrumbsContainer>
  );
};